import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { WarningBanner } from 'ui/banner';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';
import { updateDeletionPoliciesRequest } from '../actions';
import { useCustomEventsQueryContext } from '../contexts';
import { useDeletionCount, useRetentionPeriodFormData } from '../hooks';
import { createWarningMessage } from '../utils';
import { BulkActionEventList } from './BulkActionEventList';
import { RetentionPeriodForm } from './RetentionPeriodForm';

export const TEXT = {
  CONFIRM_BUTTON: 'Save retention period',
  CONFIRM_BUTTON_ID: 'retention-period-update-confirm',
  MODAL_ID: 'retention-period-update-modal',
  OPTIONS_ID: 'retention-period',
  WARNING_BANNER: `Be careful! This change may delete some of your project's saved events, and we can't recover them for you.`,
};

export const headerText = (eventNames: string[]) =>
  eventNames.length === 1
    ? `Update ${eventNames[0]}'s retention period?`
    : `Update retention periods for ${eventNames.length} events?`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WarningBannerWithMargin = styled(WarningBanner)`
  margin-top: ${spacing.base};
`;

type Props = {
  onClose: () => void;
  eventNames: string[];
};

export const RetentionPeriodUpdateModal = ({ onClose, eventNames }: Props) => {
  const dispatch = useDispatch();
  const numEvents = eventNames.length;
  const { customEvents, project } = useCustomEventsQueryContext();
  const selectedEvents = customEvents.filter((custom) => eventNames.includes(custom.eventName));

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const { error, formData, initialRetentionPeriod, retentionPeriod, setFormData } =
    useRetentionPeriodFormData(selectedEvents);

  const { deletionCount, isLoadingDeletionCount } = useDeletionCount({
    customEvents: selectedEvents,
    retentionPeriod,
  });

  const warningMessage = createWarningMessage(
    deletionCount,
    retentionPeriod,
    isLoadingDeletionCount,
  );

  const handleUpdate = async () => {
    if (!error) {
      await dispatch(
        updateDeletionPoliciesRequest({
          events: eventNames,
          isInitial: false,
          projectId: project.id,
          retentionPeriod,
        }),
      );
      onClose();
    }
  };

  useEffect(() => {
    setIsButtonDisabled(retentionPeriod === initialRetentionPeriod);
  }, [retentionPeriod]);

  return (
    <Modal
      data-test={TEXT.MODAL_ID}
      id={TEXT.MODAL_ID}
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>{headerText(eventNames)}</ModalHeader>
      <ModalBody>
        <Wrapper>
          <RetentionPeriodForm
            error={error}
            formData={formData}
            setFormData={setFormData}
            id={TEXT.OPTIONS_ID}
          />
        </Wrapper>
        {numEvents > 1 && <BulkActionEventList eventNames={eventNames} />}
        {retentionPeriod > 0 && (
          <WarningBannerWithMargin Icon={false}>{warningMessage}</WarningBannerWithMargin>
        )}
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          data-test={TEXT.CONFIRM_BUTTON_ID}
          onClick={handleUpdate}
          isDisabled={isButtonDisabled}
          size={BUTTON_SIZES.LARGE}
        >
          {TEXT.CONFIRM_BUTTON}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
